import * as Api from '@tableau/api-internal-contract-js';
import { AddInType, ShowDataTableFormat } from '../../gen-src/preslayer/Enums';
import * as PresLayer from '../../gen-src/preslayer/PresModels';
import { ErrorHelpers } from '../ErrorHelpers';
import { ApiToPresLayerEnumMappings as EnumConverter } from './EnumMappings';

/**
 * Class containing helper methods for converting from ApiPresModels to their PresLayer equivalents
 *
 * @export
 * @class ApiToPresLayerConverter
 */
export class ApiToPresLayerConverter {
  public static convertSheetPath(apiSheetPath: Api.SheetPath): PresLayer.SheetPathPresModel {
    const result: PresLayer.SheetPathPresModel = {
      sheetName: apiSheetPath.sheetName,
      isDashboard: apiSheetPath.isDashboard,
      storyboard: apiSheetPath.storyboard || '',
      storyPointId: apiSheetPath.storyPointID || 0,
    };

    return result;
  }

  private static isWorksheetExtensionLocator(locator: Api.ExtensionLocator): locator is Api.WorksheetExtensionLocator {
    return !!(locator as Api.WorksheetExtensionLocator).visualId;
  }

  private static isDashboardExtensionLocator(locator: Api.ExtensionLocator): locator is Api.DashboardExtensionLocator {
    return !!(locator as Api.DashboardExtensionLocator).dashboardPath;
  }

  public static convertExtensionLocator(apiExtensionLocator: Api.ExtensionLocator): PresLayer.AddInLocatorPresModel {
    if (ApiToPresLayerConverter.isWorksheetExtensionLocator(apiExtensionLocator)) {
      return {
        addInInstanceId: apiExtensionLocator.instanceId,
        visualIdPresModel: ApiToPresLayerConverter.convertVisualId(apiExtensionLocator.visualId),
        addInType: AddInType.Worksheet,
      };
    }

    if (ApiToPresLayerConverter.isDashboardExtensionLocator(apiExtensionLocator)) {
      return {
        addInInstanceId: apiExtensionLocator.instanceId,
        sheetPath: ApiToPresLayerConverter.convertSheetPath(apiExtensionLocator.dashboardPath),
        addInType: AddInType.Dashboard,
      };
    }

    throw ErrorHelpers.invalidValueForArgumentError(apiExtensionLocator, 'apiExtensionLocator');
  }

  public static convertVisualId(apiVisualid: Api.VisualId): PresLayer.VisualIDPresModel {
    const result = {
      worksheet: apiVisualid.worksheet,
      ...(apiVisualid.dashboard !== undefined && { dashboard: apiVisualid.dashboard }),
      ...(apiVisualid.storyboard !== undefined && { storyboard: apiVisualid.storyboard }),
      ...(apiVisualid.storyPointID !== undefined && { storyPointId: apiVisualid.storyPointID }),
      ...(apiVisualid.flipboardZoneID !== undefined && { flipboardZoneId: apiVisualid.flipboardZoneID }),
    };

    return result as PresLayer.VisualIDPresModel;
  }

  public static convertFilterValues(filterValues: Array<string>): Array<string> {
    if (filterValues.length === 0) {
      // platform code expects an empty string for clearing filter values
      return [''];
    }
    return filterValues;
  }

  public static convertFilterType(filterUpdateType: Api.FilterUpdateType): string {
    return EnumConverter.filterUpdateType.convert(filterUpdateType);
  }

  public static convertNullOption(nullOption: Api.FilterNullOption): string {
    return EnumConverter.nullOption.convert(nullOption);
  }

  public static convertFilterDomainType(filterDomainType: Api.FilterDomainType): string {
    return EnumConverter.filterDomainType.convert(filterDomainType);
  }

  public static convertShowDataTableFormat(showDataTableFormat: Api.ApiShowDataTableFormat): ShowDataTableFormat {
    return EnumConverter.showDataTableFormat.convert(showDataTableFormat);
  }

  public static convertClickThroughEnabled(clickThroughEnabled: boolean): boolean {
    // convert clickThroughEnabled to PointerEventsShouldBeSetToAuto
    // enabling click through is the inverse of PointerEventsShouldBeSetToAuto
    return !clickThroughEnabled;
  }

  public static convertVisualIdToWorksheetName(apiVisualid: Api.VisualId): string {
    return apiVisualid.worksheet;
  }

  public static convertPeriodType(periodType: Api.DateStepPeriod): string {
    return EnumConverter.periodType.convert(periodType);
  }

  public static convertDateRangeType(dateRangeType: Api.DateRangeType): string {
    return EnumConverter.dateRangeType.convert(dateRangeType);
  }

  public static convertTooltipTextRequest(tooltipTextRequest: Api.TooltipTextRequest): PresLayer.TooltipTextRequestPresModel {
    return {
      visualIdPresModel: ApiToPresLayerConverter.convertVisualId(tooltipTextRequest.visualIdPresModel),
      tupleId: tooltipTextRequest.tupleId,
    };
  }

  public static convertSelectTuplesInteraction(interaction: Api.SelectTuplesInteraction): PresLayer.SelectTuplesInteractionPresModel {
    return {
      visualIdPresModel: ApiToPresLayerConverter.convertVisualId(interaction.visualIdPresModel),
      tupleIds: interaction.tupleIds,
      selectOptions: EnumConverter.selectOptionsType.convert(interaction.selectOptions),
      ...(interaction.tooltipContext !== undefined && { tooltipContext: interaction.tooltipContext }),
    };
  }

  public static convertHoverTupleInteraction(interaction: Api.HoverTupleInteraction): PresLayer.HoverTupleInteractionPresModel {
    let hoverTuplePresModel: PresLayer.HoverTupleInteractionPresModel = {
      visualIdPresModel: ApiToPresLayerConverter.convertVisualId(interaction.visualIdPresModel),
      tupleId: interaction.tupleId,
      allowHoverActions: true,
    };

    if (interaction.allowHoverActions !== undefined) {
      hoverTuplePresModel.allowHoverActions = interaction.allowHoverActions;
    }

    if (interaction.tooltipContext) {
      hoverTuplePresModel.tooltipContext = interaction.tooltipContext;
    }

    return hoverTuplePresModel;
  }

  public static convertEncodingType(encodingType: Api.EncodingType) {
    return EnumConverter.encodingType.convert(encodingType);
  }
}
